// ============================================================
// HoverChat Admin — Magic-Link Verify Page
// Reached from the email link at /admin/auth/magic-link/verify?token=...
// Calls the API to exchange the token for a session, then reloads
// into the admin panel (App.tsx re-bootstraps auth from localStorage).
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { MessageSquare, CheckCircle2, XCircle } from 'lucide-react';

type Status = 'verifying' | 'ok' | 'error';

export function MagicLinkVerifyPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState('');

  // Guard against React 18 StrictMode double-invoking the effect in dev,
  // which would burn the single-use token on the first render.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setError('This sign-in link is missing a token.');
      return;
    }

    (async () => {
      try {
        await api.verifyMagicLink(token);
        setStatus('ok');
        // Force a full reload so App.tsx re-bootstraps auth state cleanly.
        setTimeout(() => {
          window.location.href = '/admin/';
        }, 800);
      } catch (err: any) {
        setStatus('error');
        setError(err.message || 'This sign-in link is invalid or has expired.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">HoverChat Admin</h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Signing you in…</h2>
              <p className="text-sm text-gray-500">Verifying your magic link.</p>
            </>
          )}

          {status === 'ok' && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">You're in!</h2>
              <p className="text-sm text-gray-500">Redirecting to the dashboard…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Sign-in failed</h2>
              <p className="text-sm text-gray-600 mb-6">{error}</p>
              <Link
                to="/login"
                className="inline-block w-full bg-primary text-white py-2.5 rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
              >
                Request a new link
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
